/**
 * Projects API — list / CRUD / upload / brief / extraction / graph.
 *
 * Mirrors ``backend/projects/router.py``. Schemas live in ``@services/schemas``
 * for the Zod-validated payloads; raw shapes (stats, datasheet, etc.) come
 * from ``@models/project``.
 */
import apiClient from "@lib/axios";
import type {
  Board,
  BoardGraph,
  ComponentDatasheet,
  ConnectorAnnotationInput,
  ExtractionStatus,
  Project,
  ProjectStats,
  UploadResponse,
} from "@models/project";
import {
  BoardGraphSchema,
  BoardSchema,
  ExtractionStatusSchema,
  ProjectSchema,
  UploadResponseSchema,
} from "@services/schemas";
import { z } from "zod";

/**
 * Build a `?foo=bar&baz=qux` query string from the pairs that actually have
 * a value. Used by project routes that accept both `version_id` and
 * `organisation_id` — either can be absent, both may be present.
 */
function projectQueryString(params: {
  versionId?: string | null;
  organisationId?: string | null;
}): string {
  const pairs: string[] = [];
  if (params.versionId) {
    pairs.push(`version_id=${encodeURIComponent(params.versionId)}`);
  }
  if (params.organisationId) {
    pairs.push(`organisation_id=${encodeURIComponent(params.organisationId)}`);
  }
  return pairs.length ? `?${pairs.join("&")}` : "";
}

export const projectsApi = {
  list: async (organisationId?: string | null): Promise<Project[]> => {
    const qs = projectQueryString({ organisationId });
    const { data } = await apiClient.get(`/projects${qs}`);
    return z.array(ProjectSchema).parse(data);
  },
  create: async (body: {
    name: string;
    briefMd?: string;
    organisationId?: string | null;
  }): Promise<Project> => {
    const { data } = await apiClient.post("/projects", {
      name: body.name,
      brief_md: body.briefMd ?? "",
      organisation_id: body.organisationId ?? null,
    });
    return ProjectSchema.parse(data);
  },
  /**
   * Add an empty Board to an existing wrapper Project. Idempotent on
   * (projectId, name) — re-using an existing board's name returns
   * that board unchanged. Editor+ access required server-side.
   */
  addBoard: async (
    projectId: string,
    body: { name: string; descriptionMd?: string | null },
  ): Promise<Board> => {
    const { data } = await apiClient.post(
      `/projects/${encodeURIComponent(projectId)}/boards`,
      {
        name: body.name,
        description_md: body.descriptionMd ?? null,
      },
    );
    return BoardSchema.parse(data);
  },
  /**
   * Delete a Board from a Project — cascades versions, components,
   * nets, archived source files, and the Postgres mirror row.
   * Editor+ on the project required server-side. Wrapper survives.
   */
  deleteBoard: async (projectId: string, boardId: string): Promise<void> => {
    await apiClient.delete(
      `/projects/${encodeURIComponent(projectId)}/boards/${encodeURIComponent(boardId)}`,
    );
  },
  getStats: async (
    id: string,
    versionId?: string | null,
    organisationId?: string | null,
  ): Promise<ProjectStats> => {
    const qs = projectQueryString({ versionId, organisationId });
    const { data } = await apiClient.get<ProjectStats>(
      `/projects/${encodeURIComponent(id)}/stats${qs}`,
    );
    return data;
  },
  upload: async (formData: FormData): Promise<UploadResponse> => {
    // Unified tool-based upload — one multipart request carrying
    // `source_tool` + netlist + BOM (required) + optional schematic /
    // PCB slots. Callers build the body via `buildBoardUploadFormData`
    // and append organisation_id themselves, so the same FormData works
    // for personal and org uploads.
    //
    // NOTE: do NOT add custom request headers (e.g. Idempotency-Key) here.
    // A multipart/form-data POST with only the safelisted Content-Type is a
    // CORS "simple request" and skips the preflight; adding any non-safelisted
    // header forces a preflight OPTIONS, and if the deployed CORS layer
    // doesn't echo that header the browser blocks the upload with an opaque
    // "Network Error". Server-side content-hash dedup already makes retries
    // of identical files safe, so the header bought little. See the
    // 2026-07 upload-network-error incident.
    //
    // timeout: give the request a clean upper bound instead of hanging
    // indefinitely — a stalled upload surfaces a timeout error the UI can
    // show rather than an open-ended "Uploading…" spinner. This is an
    // axios-internal setting, not a request header, so it doesn't affect CORS.
    const { data } = await apiClient.post("/projects/upload", formData, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 120_000,
    });
    return UploadResponseSchema.parse(data) as UploadResponse;
  },
  prefetchDatasheets: async (
    id: string,
    versionId?: string | null,
    organisationId?: string | null,
  ): Promise<{ message: string }> => {
    const qs = projectQueryString({ versionId, organisationId });
    const { data } = await apiClient.post<{ message: string }>(
      `/projects/${encodeURIComponent(id)}/prefetch${qs}`,
    );
    return data;
  },
  getExtractionStatus: async (
    id: string,
    versionId?: string | null,
    organisationId?: string | null,
  ): Promise<ExtractionStatus> => {
    const qs = projectQueryString({ versionId, organisationId });
    const { data } = await apiClient.get(
      `/projects/${encodeURIComponent(id)}/extraction-status${qs}`,
    );
    return ExtractionStatusSchema.parse(data);
  },
  delete: async (id: string, organisationId?: string | null): Promise<void> => {
    const qs = projectQueryString({ organisationId });
    await apiClient.delete(`/projects/${encodeURIComponent(id)}${qs}`);
  },
  /** Rename a wrapper Project. Server enforces (name, scope)
   *  uniqueness and 409s on conflict. Returns the updated Project. */
  rename: async (id: string, name: string): Promise<Project> => {
    const { data } = await apiClient.patch(
      `/projects/${encodeURIComponent(id)}`,
      { name },
    );
    return ProjectSchema.parse(data);
  },
  /** Archive a wrapper Project (soft hide). Server is idempotent. */
  archive: async (id: string): Promise<void> => {
    await apiClient.post(`/projects/${encodeURIComponent(id)}/archive`);
  },
  /** Unarchive a previously-archived wrapper Project. */
  unarchive: async (id: string): Promise<void> => {
    await apiClient.post(`/projects/${encodeURIComponent(id)}/unarchive`);
  },
  /**
   * Persist the project brief (markdown). Pass an empty string to
   * clear an existing one. Editor+ in the org scope.
   */
  updateBrief: async (
    id: string,
    briefMd: string,
    organisationId?: string | null,
  ): Promise<void> => {
    const qs = projectQueryString({ organisationId });
    await apiClient.patch(`/projects/${encodeURIComponent(id)}/brief${qs}`, {
      brief_md: briefMd,
    });
  },
  /**
   * Replace the structured per-connector annotations for a project.
   * The full annotation list is applied atomically — pass every entry
   * the project should have after the call. Annotations omitted from
   * the list are deleted. Pass `[]` to clear all annotations. Each
   * entry is keyed by `component_uuid` (Neo4j Component.id), carries
   * a typed `kind` plus a free-text `note`. Editor+ in the org scope.
   */
  updateConnectorAnnotations: async (
    id: string,
    annotations: ConnectorAnnotationInput[],
    organisationId?: string | null,
  ): Promise<void> => {
    const qs = projectQueryString({ organisationId });
    await apiClient.put(
      `/projects/${encodeURIComponent(id)}/connector-annotations${qs}`,
      { annotations },
    );
  },
  /**
   * Per-board variant of `updateConnectorAnnotations`. Required for
   * multi-board projects — the project-level endpoint 409s with
   * 'Project … has N boards' for multi-board wrappers, so the caller
   * MUST address a specific board on every save. Single-board
   * projects can use either route; for consistency the connector
   * editor always calls this one.
   */
  updateBoardConnectorAnnotations: async (
    projectId: string,
    boardId: string,
    annotations: ConnectorAnnotationInput[],
    organisationId?: string | null,
  ): Promise<void> => {
    const qs = projectQueryString({ organisationId });
    await apiClient.put(
      `/projects/${encodeURIComponent(projectId)}/boards/${encodeURIComponent(boardId)}/connector-annotations${qs}`,
      { annotations },
    );
  },
  getGraph: async (
    id: string,
    versionId?: string | null,
    organisationId?: string | null,
  ): Promise<BoardGraph> => {
    const qs = projectQueryString({ versionId, organisationId });
    const { data } = await apiClient.get(
      `/projects/${encodeURIComponent(id)}/graph${qs}`,
    );
    return BoardGraphSchema.parse(data);
  },
  getComponentDatasheet: async (
    id: string,
    ref: string,
    versionId?: string | null,
    organisationId?: string | null,
  ): Promise<ComponentDatasheet> => {
    const qs = projectQueryString({ versionId, organisationId });
    const { data } = await apiClient.get<ComponentDatasheet>(
      `/projects/${encodeURIComponent(id)}/components/${encodeURIComponent(
        ref,
      )}/datasheet${qs}`,
    );
    return data;
  },
  /**
   * Queue SheetsData extraction for a single MPN. Used by the per-row
   * "Extract" button in the BOM tab when a component's datasheet
   * status is "missing". Returns immediately; caller polls
   * `getExtractionStatus` to detect when the part flips to "ready".
   */
  extractDatasheet: async (
    projectId: string,
    mpn: string,
    organisationId?: string | null,
  ): Promise<{ status: string; mpn: string; message: string }> => {
    const qs = projectQueryString({ organisationId });
    const { data } = await apiClient.post<{
      status: string;
      mpn: string;
      message: string;
    }>(`/projects/${encodeURIComponent(projectId)}/datasheets/extract${qs}`, {
      mpn,
    });
    return data;
  },
};
